// src/components/AttivitaExtraTab.js
// ═══════════════════════════════════════════════════════════════════════════
// VARIANTI E LAVORI EXTRA (riscritta ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Revisione 15/10/2026: la variante si registra anche come OFFERTA, prima del lavoro.
// Accettazione del cliente ed esecuzione sono due fatti separati (utils/varianti.js):
// il ricavo entra solo se accettata, il costo materiali previsto solo se accettata o eseguita.
// Percorso tipico: offerta → accettata → eseguita (da fatturare) → fatturata; nella realtà
// anche eseguita → accettata dopo. Oppure "non fatturabile" (fatta a carico nostro).
// Tutto resta modificabile — l'importo spesso si stabilisce dopo — e ogni modifica lascia
// una riga nello storico (chi, quando, da → a). Dopo la fatturazione importo e fatturabilità
// si bloccano (si possono sbloccare annullando la fatturazione, che resta nello storico).
// Il budget originale non cambia mai: Budget + Varianti = Totale commessa
// (utils/varianti.js per le regole, utils/variantiSync.js per il riepilogo sul budget).
// Collezione storica: attivitaExtra (stesso nome di prima: le voci già inserite restano).
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { Plus, Trash2, X, Edit2, Printer, ChevronDown, ChevronUp } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { importoCommessa as importoContrattuale } from "../utils/economiaCommessa";
import { ORIGINI_VARIANTE, statoVariante, importoVariante, leggiImporto, riepilogoVarianti, vociStorico, eAccettata, eEseguita, costoPrevistoVariante, costoPrevistoConta, DECISORI_IMPORTO, testoValorizzataDa, dettaglioVarianti, nVarianti } from "../utils/varianti";
import { sincronizzaVariantiSuBudget } from "../utils/variantiSync";

function euro(n) { return "€ " + (Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function dataIt(s) { return s ? new Date(s + "T00:00:00").toLocaleDateString("it-IT") : "—"; }
function htmlSicuro(s) { return String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])); }
function cifraInCampo(n) {
  return n === null ? "" : n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const importoInCampo = v => cifraInCampo(importoVariante(v));

const FORM_VUOTO = () => ({ origine: "richiesta_cliente", titolo: "", descrizione: "", eseguita: false, data: "",
  accettata: false, dataAccettazione: "", autorizzazione: "", valoreEconomico: "", valorizzataDa: [], costoMaterialiPrevisto: "", fatturabile: true });

// Per lo storico: i valori "di fatto" della voce (anche per quelle registrate prima della revisione)
const comeConfronto = v => ({ ...v, valoreEconomico: importoVariante(v), valorizzataDa: Array.isArray(v.valorizzataDa) ? v.valorizzataDa : [], costoMaterialiPrevisto: costoPrevistoVariante(v), eseguita: eEseguita(v), accettata: eAccettata(v) });

const campo = { width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box", fontFamily: "'Inter',sans-serif" };
const etichetta = { fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginBottom: 3 };

export default function AttivitaExtraTab({ projectId, orgId, projectData, apriId = null, onAperto }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const commessaChiusa = projectData?.statoCommessa === "chiusa";
  const [voci, setVoci] = useState([]);
  const [budget, setBudget] = useState(null);
  const [form, setForm] = useState(null);          // null = chiuso; { ...campi, id? } = nuova o modifica
  const [salvando, setSalvando] = useState(false);
  const [fatturando, setFatturando] = useState(null); // { id, fatturaRif, dataFattura }
  const [azione, setAzione] = useState(null);         // { id, tipo: "accetta"|"esegui", data, rif }
  const [storicoAperto, setStoricoAperto] = useState({});
  const [mostraEscluse, setMostraEscluse] = useState(false);
  const [caricate, setCaricate] = useState(false);

  useEffect(() => {
    if (!projectId || !orgId) return;
    return onSnapshot(query(collection(db, "attivitaExtra"), where("projectId", "==", projectId), where("orgId", "==", orgId)),
      snap => { setVoci(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.data || b.dataOfferta || "").localeCompare(a.data || a.dataOfferta || ""))); setCaricate(true); });
  }, [projectId, orgId]);

  useEffect(() => {
    if (!projectId || !orgId) return;
    return onSnapshot(query(collection(db, "budgetCommessa"), where("projectId", "==", projectId), where("organizationId", "==", orgId)),
      snap => setBudget(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() }));
  }, [projectId, orgId]);

  const chi = currentUser?.displayName || currentUser?.email || "";
  const sincronizza = () => sincronizzaVariantiSuBudget(projectId, orgId)
    .catch(err => console.warn("Riepilogo varianti non aggiornato sul budget:", err));

  const datiCommessa = {
    projectName: projectData?.name || "", codiceCommessa: projectData?.codiceCommessa || "",
    clienteNome: projectData?.clienteNome || "",
  };

  // ── Nuova / modifica ────────────────────────────────────────────────────────
  // Aprendo il modulo (nuova variante o matita) la pagina scorre fino al modulo e lo
  // evidenzia un attimo: altrimenti, con l'elenco lungo, sembra che il clic non abbia fatto nulla
  const refForm = useRef(null);
  const refTitolo = useRef(null);
  const [aperture, setAperture] = useState(0);
  const [evidenzia, setEvidenzia] = useState(false);
  useEffect(() => {
    if (!aperture || !refForm.current) return undefined;
    refForm.current.scrollIntoView({ behavior: "smooth", block: "start" });
    const fuoco = setTimeout(() => refTitolo.current?.focus({ preventScroll: true }), 350);
    setEvidenzia(true);
    const spegni = setTimeout(() => setEvidenzia(false), 1600);
    return () => { clearTimeout(fuoco); clearTimeout(spegni); };
  }, [aperture]);

  const apriNuova = () => {
    if (commessaChiusa) { addToast("Commessa chiusa: non si registrano nuove varianti", "error"); return; }
    setForm(FORM_VUOTO());
    setAperture(n => n + 1);
  };
  const apriModifica = (v) => { setAperture(n => n + 1); setForm({
    id: v.id, origine: v.origine || "richiesta_cliente", titolo: v.titolo || "", descrizione: v.descrizione || "",
    eseguita: eEseguita(v), data: v.data || "",
    accettata: eAccettata(v), dataAccettazione: v.dataAccettazione || "", autorizzazione: v.autorizzazione || "",
    valoreEconomico: importoInCampo(v), valorizzataDa: Array.isArray(v.valorizzataDa) ? v.valorizzataDa : [], costoMaterialiPrevisto: cifraInCampo(costoPrevistoVariante(v)),
    fatturabile: v.fatturabile !== false, fatturata: statoVariante(v).id === "fatturata",
  }); };

  // Aperta dalla Cabina di Regia su una variante precisa: si apre direttamente la sua scheda
  useEffect(() => {
    if (!apriId || !caricate) return;
    const v = voci.find(x => x.id === apriId);
    if (v) apriModifica(v);
    if (onAperto) onAperto();
  }, [apriId, caricate, voci, onAperto, apriModifica]);

  const salva = async () => {
    if (!form.titolo.trim()) { addToast("Il titolo è obbligatorio", "error"); return; }
    const testoImporto = String(form.valoreEconomico || "").trim();
    const importo = testoImporto === "" ? null : leggiImporto(testoImporto);
    if (testoImporto !== "" && !(importo >= 0)) { addToast("Importo non valido: scrivi una cifra (es. 1.250,00) oppure lascia vuoto", "error"); return; }
    const testoCosto = String(form.costoMaterialiPrevisto || "").trim();
    const costo = testoCosto === "" ? null : leggiImporto(testoCosto);
    if (testoCosto !== "" && !(costo >= 0)) { addToast("Costo materiali previsto non valido: scrivi una cifra oppure lascia vuoto", "error"); return; }
    const eseguita = form.fatturata ? true : !!form.eseguita;
    const accettata = form.fatturata ? true : !!form.accettata;
    if (eseguita && !form.data) { addToast("Indica la data di esecuzione del lavoro", "error"); return; }
    if (accettata && !form.autorizzazione.trim()) { addToast("Indica il riferimento dell'accettazione del cliente (es. email del 12/10, ordine n. 45)", "error"); return; }
    const dati = {
      origine: form.origine, titolo: form.titolo.trim(), descrizione: form.descrizione.trim(),
      eseguita, data: eseguita ? form.data : "",
      accettata, dataAccettazione: accettata ? (form.dataAccettazione || toLocalDateStr(new Date())) : "",
      autorizzazione: form.autorizzazione.trim(), costoMaterialiPrevisto: costo,
      valorizzataDa: importo === null ? [] : Object.keys(DECISORI_IMPORTO).filter(k => (form.valorizzataDa || []).includes(k)),
      ...(form.fatturata ? {} : { valoreEconomico: importo, fatturabile: !!form.fatturabile }),
    };
    setSalvando(true);
    try {
      if (form.id) {
        // Rileggo la variante: se nel frattempo un collega l'ha fatturata, importo e
        // fatturabilità sono bloccati e non si sovrascrivono con il modulo aperto prima.
        const fresca = await getDoc(doc(db, "attivitaExtra", form.id));
        if (!fresca.exists()) { addToast("La variante è stata eliminata da un collega", "error"); setForm(null); return; }
        const prima = { id: fresca.id, ...fresca.data() };
        if (!form.fatturata && statoVariante(prima).id === "fatturata") {
          addToast("Nel frattempo la variante è stata fatturata: importo e fatturabilità sono bloccati. Riapri la scheda.", "error");
          setForm(null); return;
        }
        const righe = vociStorico(comeConfronto(prima), dati, chi, new Date().toISOString());
        if (righe.length === 0) { setForm(null); return; }
        await updateDoc(doc(db, "attivitaExtra", form.id), {
          ...dati, ...datiCommessa,
          // le voci create prima di ottobre 2026 avevano stati "Da valutare/Approvata": qui si allineano
          ...(prima.stato !== "Fatturata" && prima.stato !== "Respinta" ? { stato: eseguita ? "Eseguita" : "Offerta" } : {}),
          storico: arrayUnion(...righe), updatedAt: serverTimestamp(),
        });
        addToast("Variante aggiornata", "success");
      } else {
        await addDoc(collection(db, "attivitaExtra"), {
          projectId, orgId, ...dati, ...datiCommessa, stato: eseguita ? "Eseguita" : "Offerta", fatturata: false,
          dataOfferta: toLocalDateStr(new Date()),
          creatoDaUid: currentUser?.uid || "", creatoDaNome: chi,
          storico: [{ il: new Date().toISOString(), chi, campo: "Registrata", da: "—",
            a: `${eseguita ? "lavoro eseguito" : "offerta"}${accettata ? ", accettata" : ""} · ${importo === null ? "senza importo" : euro(importo)}` }],
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
        addToast(importo === null ? "Variante registrata — ricordati di valorizzarla"
          : accettata ? "Variante registrata" : "Variante registrata — entra nel totale commessa quando il cliente la accetta", "success");
      }
      setForm(null);
      sincronizza();
    } catch (err) {
      addToast(messaggioErrore(err, "salvare la variante"), "error");
    } finally {
      setSalvando(false);
    }
  };

  // ── Accettazione / esecuzione con un clic ───────────────────────────────────
  const confermaAzione = async () => {
    if (salvando || !azione) return;
    const v = voci.find(x => x.id === azione.id);
    if (!v) return;
    if (!azione.data) { addToast("Indica la data", "error"); return; }
    if (azione.tipo === "accetta" && !azione.rif.trim()) { addToast("Indica il riferimento dell'accettazione (es. email del 12/10, ordine n. 45)", "error"); return; }
    const dopo = azione.tipo === "accetta"
      ? { accettata: true, dataAccettazione: azione.data, autorizzazione: azione.rif.trim() }
      : { eseguita: true, data: azione.data, ...(v.stato !== "Fatturata" ? { stato: "Eseguita" } : {}) };
    setSalvando(true);
    try {
      await updateDoc(doc(db, "attivitaExtra", v.id), {
        ...dopo, storico: arrayUnion(...vociStorico(comeConfronto(v), dopo, chi, new Date().toISOString())), updatedAt: serverTimestamp(),
      });
      addToast(azione.tipo === "accetta" ? "Variante accettata: entra nel totale commessa" : "Lavoro segnato come eseguito", "success");
      setAzione(null);
      sincronizza();
    } catch (err) { addToast(messaggioErrore(err, "aggiornare la variante"), "error"); }
    finally { setSalvando(false); }
  };

  // ── Fatturazione ────────────────────────────────────────────────────────────
  const confermaFatturata = async () => {
    if (salvando) return;
    const v = voci.find(x => x.id === fatturando.id);
    if (!v) return;
    setSalvando(true);
    const dopo = { stato: "Fatturata", fatturaRif: fatturando.fatturaRif.trim(), dataFattura: fatturando.dataFattura || "" };
    try {
      await updateDoc(doc(db, "attivitaExtra", v.id), {
        ...dopo, fatturata: true, fatturataDaNome: chi, fatturataIl: serverTimestamp(),
        storico: arrayUnion(...vociStorico(v, dopo, chi, new Date().toISOString())), updatedAt: serverTimestamp(),
      });
      addToast("Variante segnata come fatturata", "success");
      setFatturando(null);
      sincronizza();
    } catch (err) { addToast(messaggioErrore(err, "segnare la fatturazione"), "error"); }
    finally { setSalvando(false); }
  };

  const annullaFatturazione = async (v) => {
    if (salvando) return;
    if (!(await confirm(`Annullare la fatturazione di "${v.titolo}"? La variante torna "da fatturare" e l'operazione resta nello storico.`))) return;
    const dopo = { stato: "Eseguita", fatturaRif: "", dataFattura: "" };
    setSalvando(true);
    try {
      await updateDoc(doc(db, "attivitaExtra", v.id), {
        ...dopo, fatturata: false,
        storico: arrayUnion(...vociStorico(v, dopo, chi, new Date().toISOString())), updatedAt: serverTimestamp(),
      });
      sincronizza();
    } catch (err) { addToast(messaggioErrore(err, "annullare la fatturazione"), "error"); }
    finally { setSalvando(false); }
  };

  const elimina = async (v) => {
    if (statoVariante(v).id === "fatturata") { addToast("Una variante fatturata non si elimina: annulla prima la fatturazione", "error"); return; }
    if (!(await confirm(`Eliminare la variante "${v.titolo}"? Usalo solo per una voce inserita per errore.`))) return;
    try {
      await deleteDoc(doc(db, "attivitaExtra", v.id));
      addToast("Variante eliminata", "success");
      sincronizza();
    } catch (err) { addToast(messaggioErrore(err, "eliminare la variante"), "error"); }
  };

  // ── Riepilogo per il cliente (stampa / PDF dal browser) ─────────────────────
  const stampaRiepilogo = () => {
    const d = dettaglioVarianti(voci);
    const contratto = importoContrattuale(budget);
    const r = riepilogoVarianti(voci);
    const rigaVal = x => `<tr><td><b>${htmlSicuro(x.titolo)}</b>${x.descrizione ? `<div class="d">${htmlSicuro(x.descrizione)}</div>` : ""}<div class="d">${htmlSicuro(x.origine)}${x.aCaricoImpresa ? " · a carico dell'impresa" : ""}</div></td><td class="n">${htmlSicuro(euro(x.importo))}</td><td class="c">${x.autorizzata ? "✔ autorizzata" + (x.riferimento ? `<div class="d">${htmlSicuro(x.riferimento)}</div>` : "") : "<b>da autorizzare</b>"}</td></tr>`;
    const rigaDa = x => `<tr><td><b>${htmlSicuro(x.titolo)}</b>${x.descrizione ? `<div class="d">${htmlSicuro(x.descrizione)}</div>` : ""}</td><td class="c">${htmlSicuro(x.origine)}</td></tr>`;
    const w = window.open("", "_blank");
    if (!w) { addToast("Il browser ha bloccato la finestra di stampa", "error"); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Varianti richieste o indispensabili</title><style>
      body{font-family:Arial,sans-serif;color:#111;margin:32px;font-size:13px}h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;margin:20px 0 6px}
      .s{color:#555;margin-bottom:14px}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #ddd;padding:8px;text-align:left;vertical-align:top}
      th{background:#f4f4f5;font-size:11px;text-transform:uppercase}.n{text-align:right;white-space:nowrap}.c{white-space:nowrap}.d{color:#555;font-size:12px;margin-top:2px}
      .tot{margin:4px 0;font-size:13px}</style></head><body>
      <h1>Varianti richieste o indispensabili</h1>
      <div class="s">${htmlSicuro(datiCommessa.codiceCommessa ? "[" + datiCommessa.codiceCommessa + "] " : "")}${htmlSicuro(datiCommessa.projectName)}${datiCommessa.clienteNome ? " — " + htmlSicuro(datiCommessa.clienteNome) : ""}<br>Situazione al ${new Date().toLocaleDateString("it-IT")}</div>
      ${contratto > 0 ? `<div class="tot">Importo contrattuale <b>${htmlSicuro(euro(contratto))}</b></div>` : ""}
      <div class="tot">Varianti richieste o indispensabili: <b>n. ${nVarianti(d.n)} variant${d.n === 1 ? "e" : "i"}</b> come da dettaglio, per un valore calcolato pari a <b>${htmlSicuro(euro(d.valoreCalcolato))}</b>${d.valoreAutorizzato !== d.valoreCalcolato ? ` (di cui autorizzate ${htmlSicuro(euro(d.valoreAutorizzato))})` : ""}</div>
      ${contratto > 0 ? `<div class="tot">Totale commessa (contratto + varianti autorizzate) <b>${htmlSicuro(euro(contratto + r.totale))}</b></div>` : ""}
      ${d.valorizzate.length ? `<h2>Varianti valorizzate</h2><table><thead><tr><th>Voce</th><th class="n">Importo</th><th>Autorizzazione</th></tr></thead><tbody>${d.valorizzate.map(rigaVal).join("")}</tbody></table>` : ""}
      ${d.daValorizzare.length ? `<h2>Varianti da valorizzare</h2><table><thead><tr><th>Voce</th><th>Tipo</th></tr></thead><tbody>${d.daValorizzare.map(rigaDa).join("")}</tbody></table>` : ""}
      ${d.n === 0 ? "<p>Nessuna variante.</p>" : ""}
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  // ── Calcoli per la vista ────────────────────────────────────────────────────
  const attive = voci.filter(v => statoVariante(v).id !== "esclusa");
  const escluse = voci.filter(v => statoVariante(v).id === "esclusa");
  const r = riepilogoVarianti(voci);
  const contratto = importoContrattuale(budget);

  const card = (titolo, valore, colore, sotto, bg = "#faf5ff") => (
    <div style={{ background: bg, borderRadius: 10, padding: "10px 14px" }}>
      <div style={{ fontSize: 9.5, color: "#9ca3af", textTransform: "uppercase", fontWeight: 700 }}>{titolo}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: colore }}>{valore}</div>
      {sotto && <div style={{ fontSize: 10.5, color: "#6b7280", marginTop: 1 }}>{sotto}</div>}
    </div>
  );

  const rigaVariante = (v) => {
    const o = ORIGINI_VARIANTE[v.origine] || ORIGINI_VARIANTE.richiesta_cliente;
    const st = statoVariante(v);
    const imp = importoVariante(v);
    const storico = Array.isArray(v.storico) ? v.storico : [];
    return (
      <div key={v.id} style={{ background: "#fff", border: `1px solid ${st.id === "daValorizzare" ? "#fdba74" : "#e5e7eb"}`, borderRadius: 10, padding: "12px 16px", marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8, background: o.colore + "1f", color: o.colore }}>{o.label}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8, background: st.bg, color: st.colore }}>{st.label}</span>
              {v.fatturaRif && <span style={{ fontSize: 10, color: "#059669" }}>Fattura {v.fatturaRif}{v.dataFattura ? ` del ${dataIt(v.dataFattura)}` : ""}</span>}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{v.titolo}</div>
            {v.descrizione && <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2, whiteSpace: "pre-wrap" }}>{v.descrizione}</div>}
            <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 4 }}>
              {eEseguita(v) ? `Eseguita il ${dataIt(v.data)}` : "Lavoro da eseguire"}
              {" · "}{eAccettata(v) ? `accettata${v.dataAccettazione ? ` il ${dataIt(v.dataAccettazione)}` : ""}${v.autorizzazione ? ` (${v.autorizzazione})` : ""}` : "non ancora accettata dal cliente"}
              {testoValorizzataDa(v) ? ` · importo deciso da ${testoValorizzataDa(v)}` : ""}
              {v.creatoDaNome ? ` · registrata da ${v.creatoDaNome}` : ""}
            </div>
            {costoPrevistoVariante(v) !== null && (
              <div style={{ fontSize: 10.5, color: costoPrevistoConta(v) ? "#0e7490" : "#9ca3af", marginTop: 2 }}>
                Costo materiali previsto {euro(costoPrevistoVariante(v))}{costoPrevistoConta(v) ? " · conta nel costo previsto" : " · non conta finché non è accettata o eseguita"}
              </div>
            )}
            {storico.length > 0 && (
              <button onClick={() => setStoricoAperto(s => ({ ...s, [v.id]: !s[v.id] }))}
                style={{ marginTop: 6, background: "none", border: "none", padding: 0, fontSize: 10.5, color: "#7c3aed", cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}>
                {storicoAperto[v.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Storico ({storico.length})
              </button>
            )}
            {storicoAperto[v.id] && (
              <div style={{ marginTop: 6, background: "#f9fafb", borderRadius: 8, padding: "6px 10px" }}>
                {storico.slice().reverse().map((s, i) => (
                  <div key={i} style={{ fontSize: 10.5, color: "#4b5563", padding: "2px 0" }}>
                    <span style={{ color: "#9ca3af" }}>{s.il ? new Date(s.il).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : ""} · {s.chi || "—"}</span> — <b>{s.campo}</b>: {s.da} → {s.a}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: imp === null ? "#c2410c" : (st.id === "nonFatturabile" || st.id === "offerta" || st.id === "inAttesa") ? "#9ca3af" : "#059669", marginBottom: 6 }}>
              {imp === null ? "da valorizzare" : euro(imp)}
            </div>
            <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
              {!commessaChiusa && !eAccettata(v) && st.id !== "nonFatturabile" && (
                <button onClick={() => setAzione({ id: v.id, tipo: "accetta", data: toLocalDateStr(new Date()), rif: v.autorizzazione || "" })}
                  title="Registra l'accettazione del cliente, con data e riferimento"
                  style={{ fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 6, border: "1px solid #1d4ed8", background: "#fff", color: "#1d4ed8", cursor: "pointer" }}>Segna accettata</button>
              )}
              {!commessaChiusa && !eEseguita(v) && (
                <button onClick={() => setAzione({ id: v.id, tipo: "esegui", data: toLocalDateStr(new Date()), rif: "" })}
                  style={{ fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 6, border: "1px solid #0e7490", background: "#ecfeff", color: "#0e7490", cursor: "pointer" }}>Segna eseguita</button>
              )}
              {st.id === "valorizzata" && (
                <button onClick={() => setFatturando({ id: v.id, fatturaRif: "", dataFattura: toLocalDateStr(new Date()) })}
                  style={{ fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 6, border: "1px solid #059669", background: "#ecfdf5", color: "#059669", cursor: "pointer" }}>Segna fatturata</button>
              )}
              {st.id === "fatturata" && (
                <button onClick={() => annullaFatturazione(v)} style={{ fontSize: 11, padding: "4px 9px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer" }}>Annulla fatturazione</button>
              )}
              <button onClick={() => apriModifica(v)} title="Modifica" style={{ background: "none", border: "none", cursor: "pointer", color: "#7c3aed", padding: 4 }}><Edit2 size={14} /></button>
              {st.id !== "fatturata" && <button onClick={() => elimina(v)} title="Elimina" style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 4 }}><Trash2 size={14} /></button>}
            </div>
          </div>
        </div>
        {azione?.id === v.id && (
          <div style={{ marginTop: 10, background: azione.tipo === "accetta" ? "#eff6ff" : "#ecfeff", borderRadius: 8, padding: 10, display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ width: 150 }}>
              <label style={etichetta}>{azione.tipo === "accetta" ? "Data accettazione" : "Data di esecuzione"}</label>
              <input type="date" value={azione.data} onChange={e => setAzione(a => ({ ...a, data: e.target.value }))} style={campo} />
            </div>
            {azione.tipo === "accetta" && (
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={etichetta}>Riferimento accettazione *</label>
                <input value={azione.rif} onChange={e => setAzione(a => ({ ...a, rif: e.target.value }))} placeholder="es. email del 12/10, ordine n. 45, firma sul verbale" style={campo} />
              </div>
            )}
            <button onClick={confermaAzione} disabled={salvando} style={{ opacity: salvando ? .6 : 1, padding: "8px 14px", borderRadius: 8, border: "none", background: azione.tipo === "accetta" ? "#1d4ed8" : "#0e7490", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Conferma</button>
            <button onClick={() => setAzione(null)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", fontSize: 12, cursor: "pointer" }}>Annulla</button>
          </div>
        )}
        {fatturando?.id === v.id && (
          <div style={{ marginTop: 10, background: "#ecfdf5", borderRadius: 8, padding: 10, display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={etichetta}>Numero fattura</label>
              <input value={fatturando.fatturaRif} onChange={e => setFatturando(f => ({ ...f, fatturaRif: e.target.value }))} placeholder="es. 125/2026" style={campo} />
            </div>
            <div style={{ width: 150 }}>
              <label style={etichetta}>Data fattura</label>
              <input type="date" value={fatturando.dataFattura} onChange={e => setFatturando(f => ({ ...f, dataFattura: e.target.value }))} style={campo} />
            </div>
            <button onClick={confermaFatturata} disabled={salvando} style={{ opacity: salvando ? .6 : 1,  padding: "8px 14px", borderRadius: 8, border: "none", background: "#059669", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Conferma</button>
            <button onClick={() => setFatturando(null)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", fontSize: 12, cursor: "pointer" }}>Annulla</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: 20, height: "100%", overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 10 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: "#1e1b4b", margin: 0 }}>➕ Varianti e lavori extra</h3>
          <p style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2, maxWidth: 640 }}>
            Registra qui le varianti fuori contratto, <b>richieste dal cliente</b> o <b>tecniche indispensabili</b>: come offerta prima del lavoro oppure a lavoro fatto. Il budget originale non cambia:
            solo le varianti <b>accettate dal cliente</b> formano il <b>totale commessa</b>, su cui si calcolano ricavi e margine.
            Il costo materiali previsto conta solo se la variante è accettata o eseguita.
          </p>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {attive.length > 0 && (
            <button onClick={stampaRiepilogo} title="Riepilogo da mandare al cliente" style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
              <Printer size={14} /> Riepilogo per il cliente
            </button>
          )}
          <button onClick={apriNuova} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
            <Plus size={14} /> Nuova variante
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 12 }}>
        {card("Importo contrattuale", contratto > 0 ? euro(contratto) : "—", "#111827", "budget originale", "#f9fafb")}
        {card("Varianti accettate", euro(r.totale), "#7c3aed", r.nAccettateDaEseguire > 0 ? `di cui da eseguire ${euro(r.accettateDaEseguire)}` : `${r.nDaFatturare + r.nFatturate} voc${r.nDaFatturare + r.nFatturate === 1 ? "e" : "i"}`)}
        {card("In attesa di accettazione", euro(r.inAttesa), r.inAttesa > 0 ? "#b45309" : "#9ca3af", "fuori dal totale e dal margine", "#fffbeb")}
        {card("Totale commessa", euro(contratto + r.totale), "#1e1b4b", "contratto + varianti accettate", "#eef2ff")}
        {card("Da fatturare", euro(r.daFatturare), r.daFatturare > 0 ? "#7c3aed" : "#9ca3af", r.fatturato > 0 ? `già fatturato ${euro(r.fatturato)}` : null)}
      </div>

      {(r.nOfferte + r.nEseguiteDaAccettare) > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10, padding: "9px 14px", marginBottom: 8, fontSize: 12, color: "#92400e" }}>
          ⏳ <b>{r.nOfferte + r.nEseguiteDaAccettare} variant{r.nOfferte + r.nEseguiteDaAccettare === 1 ? "e" : "i"} per {euro(r.inAttesa)} in attesa di accettazione del cliente</b>
          {r.nEseguiteDaAccettare > 0 ? ` (${r.nEseguiteDaAccettare} già eseguit${r.nEseguiteDaAccettare === 1 ? "a" : "e"})` : ""}: {r.nOfferte + r.nEseguiteDaAccettare === 1 ? "non entra nel totale commessa né nel margine finché il cliente non la accetta" : "non entrano nel totale commessa né nel margine finché il cliente non le accetta"}.
        </div>
      )}
      {r.nDaValorizzare > 0 && (
        <div style={{ background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 10, padding: "9px 14px", marginBottom: 12, fontSize: 12, color: "#9a3412" }}>
          ⚠️ <b>{r.nDaValorizzare} variant{r.nDaValorizzare === 1 ? "e" : "i"} senza importo</b>: {r.nDaValorizzare === 1 ? "valorizzala per poterla far accettare al cliente e farla entrare nel totale commessa" : "valorizzale per poterle far accettare al cliente e farle entrare nel totale commessa"}.
        </div>
      )}

      {form && (
        <div ref={refForm} style={{ background: "#f9fafb", border: `1px solid ${evidenzia ? "#7c3aed" : "#e5e7eb"}`, boxShadow: evidenzia ? "0 0 0 3px rgba(124,58,237,.18)" : "none", transition: "border-color .4s, box-shadow .4s", borderRadius: 12, padding: 16, marginBottom: 16, scrollMarginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1e1b4b" }}>{form.id ? "Modifica variante" : "Nuova variante"}</span>
            <button onClick={() => setForm(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af" }}><X size={16} /></button>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {Object.values(ORIGINI_VARIANTE).map(o => (
              <button key={o.id} type="button" onClick={() => setForm(f => ({ ...f, origine: o.id }))}
                style={{ flex: 1, padding: 8, borderRadius: 8, border: `1px solid ${form.origine === o.id ? o.colore : "#e5e7eb"}`, background: form.origine === o.id ? o.colore + "1f" : "#fff", color: form.origine === o.id ? o.colore : "#6b7280", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {o.label}
              </button>
            ))}
          </div>
          <label style={etichetta}>Titolo *</label>
          <input ref={refTitolo} value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} placeholder="es. Ricablaggio aggiuntivo quadro B" style={{ ...campo, marginBottom: 8 }} />
          <label style={etichetta}>Descrizione del lavoro</label>
          <textarea value={form.descrizione} onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))} rows={3} style={{ ...campo, marginBottom: 8, resize: "vertical" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, marginBottom: 10 }}>
            <div>
              <label style={etichetta}>Importo offerta € (anche dopo)</label>
              <input value={form.valoreEconomico} disabled={form.fatturata} onChange={e => setForm(f => ({ ...f, valoreEconomico: e.target.value }))}
                placeholder="Vuoto = da valorizzare" style={{ ...campo, background: form.fatturata ? "#f3f4f6" : "#fff" }} />
              {String(form.valoreEconomico || "").trim() !== "" && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 5, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Importo deciso da:</span>
                  {Object.entries(DECISORI_IMPORTO).map(([k, l]) => (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#374151", cursor: "pointer" }}>
                      <input type="checkbox" checked={(form.valorizzataDa || []).includes(k)}
                        onChange={e => setForm(f => ({ ...f, valorizzataDa: e.target.checked ? [...(f.valorizzataDa || []), k] : (f.valorizzataDa || []).filter(x => x !== k) }))} />
                      {l}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label style={etichetta}>Costo materiali previsto €</label>
              <input value={form.costoMaterialiPrevisto} onChange={e => setForm(f => ({ ...f, costoMaterialiPrevisto: e.target.value }))}
                placeholder="facoltativo" style={campo} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10, marginBottom: 8 }}>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#0e7490", cursor: form.fatturata ? "default" : "pointer" }}>
                <input type="checkbox" checked={form.fatturata || form.eseguita} disabled={form.fatturata}
                  onChange={e => setForm(f => ({ ...f, eseguita: e.target.checked, data: e.target.checked ? (f.data || toLocalDateStr(new Date())) : f.data }))} />
                Lavoro eseguito
              </label>
              {(form.fatturata || form.eseguita) ? (
                <div style={{ marginTop: 6 }}>
                  <label style={etichetta}>Data di esecuzione *</label>
                  <input type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} style={campo} />
                </div>
              ) : <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Senza spunta è un'offerta: il lavoro si segna come eseguito quando è fatto.</div>}
            </div>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#1d4ed8", cursor: form.fatturata ? "default" : "pointer" }}>
                <input type="checkbox" checked={form.fatturata || form.accettata} disabled={form.fatturata}
                  onChange={e => setForm(f => ({ ...f, accettata: e.target.checked, dataAccettazione: e.target.checked ? (f.dataAccettazione || toLocalDateStr(new Date())) : f.dataAccettazione }))} />
                Accettata dal cliente
              </label>
              {(form.fatturata || form.accettata) ? (
                <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 6, marginTop: 6 }}>
                  <div>
                    <label style={etichetta}>Data</label>
                    <input type="date" value={form.dataAccettazione} onChange={e => setForm(f => ({ ...f, dataAccettazione: e.target.value }))} style={campo} />
                  </div>
                  <div>
                    <label style={etichetta}>Riferimento *</label>
                    <input value={form.autorizzazione} onChange={e => setForm(f => ({ ...f, autorizzazione: e.target.value }))} placeholder="es. email del 12/10, ordine n. 45" style={campo} />
                  </div>
                </div>
              ) : <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Finché non è accettata non entra nel totale commessa né nel margine.</div>}
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", marginBottom: 6, cursor: form.fatturata ? "default" : "pointer" }}>
            <input type="checkbox" checked={form.fatturabile} disabled={form.fatturata} onChange={e => setForm(f => ({ ...f, fatturabile: e.target.checked }))} />
            Fatturabile al cliente <span style={{ color: "#9ca3af" }}>(togli la spunta se il lavoro è a carico vostro: i costi restano in commessa, il ricavo è zero)</span>
          </label>
          {form.fatturata && <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>Variante già fatturata: importo e fatturabilità sono bloccati. Per cambiarli annulla prima la fatturazione.</div>}
          {form.id && <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>Ogni modifica resta nello storico della variante, con nome e data.</div>}
          <button onClick={salva} disabled={salvando} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
            {salvando ? "Salvo..." : "Salva"}
          </button>
        </div>
      )}

      {attive.length === 0
        ? <div style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 12.5 }}>Nessuna variante su questa commessa.</div>
        : attive.map(rigaVariante)}

      {escluse.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <button onClick={() => setMostraEscluse(m => !m)} style={{ background: "none", border: "none", padding: 0, fontSize: 11.5, color: "#6b7280", cursor: "pointer" }}>
            {mostraEscluse ? "▾" : "▸"} {escluse.length} voc{escluse.length === 1 ? "e respinta" : "i respinte"} (registrate prima di ottobre 2026, non contano nei totali)
          </button>
          {mostraEscluse && <div style={{ marginTop: 8, opacity: .7 }}>{escluse.map(rigaVariante)}</div>}
        </div>
      )}
    </div>
  );
}
